import { ArrowUpDownIcon } from 'lucide-react';
import React from 'react';
import { BonusCreatorState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/BonusCreator.state';
import { FilterMenu } from 'src/domains/casino/webview/bonus-engine/campaign-list/filters/Filters';
import { PaginationTable } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { Input } from 'src/domains/common/components/input/input';
import { Switch } from 'src/domains/common/components/switch/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from 'src/domains/common/components/table/table';
import { observer } from 'src/utils/mobx-react';
import { BonusCreationsTableRow } from './BonusCreationsTableRow';

export interface BonusCreationsTablePropsType {
    bonusCreatorState: BonusCreatorState;
}

export const BonusCreationsTable = observer(
    'BonusCreationsTable',
    ({ bonusCreatorState }: BonusCreationsTablePropsType) => {
        const {
            expandAllWithDescription,
            isAllExpanded,
            expandedRows,
            paginatedData,
            filter,
            setFilter,
            setSortConfig,
            toggleRowExpansion,
            paginationConfig,
        } = bonusCreatorState;

        return (
            <div className='flex flex-col overflow-hidden flex-nowrap min-h-[400px]'>
                <div className='flex items-center justify-between'>
                    <Switch
                        label='Expand All'
                        labelPosition='right'
                        checked={isAllExpanded}
                        onChange={expandAllWithDescription}
                        dataTest='expand-all-switch'
                        dataTestSpan='expand-all-toggle'
                    />

                    <div className='flex items-center gap-4 py-4'>
                        <Input
                            placeholder='Search'
                            formInputState={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className='max-w-sm py-2'
                            errors={[]}
                            dataTest='search-input'
                        />

                        <FilterMenu
                            campaignListState={bonusCreatorState}
                            submenuPosition='left'
                        />
                    </div>
                </div>
                <div className='relative w-full overflow-auto border border-solid rounded-md border-border'>
                    <Table className='table-fixed'>
                        <TableHeader className='sticky top-0 bg-white z-[1] shadow-b-shadow'>
                            <TableRow className='!border-b-0'>
                                <TableHead className='!px-2 h-14 w-12'>&nbsp;</TableHead>

                                <TableHead className='py-0 h-14'>
                                    <div
                                        className='cursor-pointer flex items-center gap-2.5'
                                        onClick={() => setSortConfig('name')}
                                    >
                                        Name
                                        <ArrowUpDownIcon className='size-4' />
                                    </div>
                                </TableHead>

                                <TableHead className='py-0 h-14'>
                                    <div
                                        className='cursor-pointer flex items-center gap-2.5'
                                        onClick={() => setSortConfig('rewardName')}
                                    >
                                        Reward Type
                                        <ArrowUpDownIcon className='size-4' />
                                    </div>
                                </TableHead>

                                <TableHead className='py-0 h-14'>
                                    <div
                                        className='cursor-pointer flex items-center gap-2.5'
                                        onClick={() => setSortConfig('rewardExpiryDisplay')}
                                    >
                                        Reward Expiry
                                        <ArrowUpDownIcon className='size-4' />
                                    </div>
                                </TableHead>

                                <TableHead className='py-0 h-14 w-[120px]'>
                                    <div
                                        className='cursor-pointer flex items-center gap-2.5'
                                        onClick={() => setSortConfig('status')}
                                    >
                                        Status
                                        <ArrowUpDownIcon className='size-4' />
                                    </div>
                                </TableHead>

                                <TableHead className='py-0 !px-2 h-14 w-[144px]'>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map((bonusCreation, index) => {
                                    const isExpanded =
                                        expandedRows.includes(bonusCreation.id) && bonusCreation.terms !== '';
                                    const rowClass = index % 2 === 0 ? 'bg-white' : 'bg-muted/50';

                                    return (
                                        <BonusCreationsTableRow
                                            key={bonusCreation.id}
                                            isExpanded={isExpanded}
                                            rowClass={rowClass}
                                            toggleRowExpansion={toggleRowExpansion}
                                            bonusCreation={bonusCreation}
                                        />
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className='h-24 text-center'
                                        data-test='no-results'
                                    >
                                        No results.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <PaginationTable config={paginationConfig} />
            </div>
        );
    }
);
